// src/layouts/DriverLayout.jsx
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Truck, Clock, Menu, UserCircle } from "lucide-react";
import { useDriver } from "../context/DriverContext";

export default function DriverLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);

  const { driver, loading, logout } = useDriver(); // use context
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !driver) {
      navigate("/driver-login");
    }
  }, [driver, loading, navigate]);

  // Close sidebar on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/driver-login");
  };

  const isActive = (path) => location.pathname === path;

  if (loading) return <div>Loading...</div>; // optional loading state

  return (
    <div className="flex relative overflow-hidden min-h-full">
      {/* Overlay on mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          bg-gray-800 text-white z-50 transform top-0 left-0 fixed md:static transition-transform duration-200 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
          w-64
        `}
      >
        <div className="h-screen md:min-h-full md:h-auto overflow-y-auto p-6">
          <div className="mb-8 flex border-b border-gray-500 pb-4">
            <img
              src="/images/logos/Exudee-logo-white.svg"
              alt="Logo"
              className="h-14 w-auto"
            />
          </div>

          <h2 className="text-xl font-bold mb-6 mt-3">Driver Panel</h2>

          <nav className="flex flex-col gap-3">
            <button
              className={`flex items-center gap-2 p-2 rounded ${
                isActive("/driver/interface") ? "bg-gray-700" : "hover:bg-gray-700"
              }`}
              onClick={() => {
                navigate("/driver/interface");
                setIsSidebarOpen(false);
              }}
            >
              <Truck className="w-5 h-5" />
              Dashboard
            </button>

            <button
              className={`flex items-center gap-2 p-2 rounded ${
                isActive("/driver/history") ? "bg-gray-700" : "hover:bg-gray-700"
              }`}
              onClick={() => {
                navigate("/driver/history");
                setIsSidebarOpen(false);
              }}
            >
              <Clock className="w-5 h-5" />
              Delivery History
            </button>

            <button
              className="flex items-center gap-2 p-2 hover:bg-gray-700 rounded"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </nav>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 bg-gray-100 min-h-screen overflow-x-auto">
        {/* Top Navbar */}
        <div className="flex justify-between md:justify-end items-center bg-white border-b p-4 shadow-sm sticky top-0 z-40">
          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 pr-3 text-gray-700 hover:text-black"
            >
              <UserCircle size={30} />
              <span className="font-medium">{driver?.name || "Driver"}</span>
            </button>
            {showDropdown && (
              <div className="absolute left-0 md:right-0 mt-2 w-40 bg-white rounded shadow z-50 border">
                <button
                  onClick={() => {
                    navigate("/driver/profile");
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                >
                  <UserCircle size={16} /> Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600 flex items-center gap-2"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu toggle */}
          <div className="md:hidden">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="bg-gray-800 text-white p-2 rounded shadow"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
